import { useTranslation } from 'react-i18next';
import {
  type ConsentPreferences,
  CookieConsentBanner,
  getConsentCookieValue,
  loadGTMScript,
  setDefaultGTMConsent,
  updateConsentFromCookie,
} from '@fastnorth/kraken/components/cookie-consent';
import initRum from '@fastnorth/datadog/initRum';
import { KEYS, SERVICE_NAMES } from '../../config/constants.ts';
import getEnvFromHostname from '../../utils/getEnvFromHostname.ts';
import { COOKIE_POLICY_URL, GTM_CONTAINER_ID } from './consts.ts';

setDefaultGTMConsent();
updateConsentFromCookie();

if (getConsentCookieValue()) {
  loadGTMScript('GTM-T7T88D8');
}

const handleConsentApplied = (prefs: ConsentPreferences): void => {
  if (prefs.analyticsAllowed) {
    initRum({
      applicationId: KEYS.dataDogRUMApplicationId,
      clientToken: KEYS.dataDogRUMClientToken,
      service: SERVICE_NAMES.dataDogRUM,
      applicationVersion: import.meta.env.VERSION,
      env: getEnvFromHostname(),
      allowedTracingUrls: [
        'https://results.raceroster.com',
        'https://results.ue1dev1.rostrr.net',
        'https://results.ue1dev2.rostrr.net',
      ],
    });
  }
};

const TranslatedCookieConsentBanner = (): React.JSX.Element | null => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.components.cookie_consent' });
  const { pathname } = window.location;
  const isCertificatePreview =
    pathname.match(new RegExp(`/events/[a-zA-Z0-9]+/finisher-certificate`)) !== null;

  if (isCertificatePreview) return null;

  return (
    <CookieConsentBanner
      gtmContainerId={GTM_CONTAINER_ID}
      cookiePolicyUrl={COOKIE_POLICY_URL}
      onConsentApplied={handleConsentApplied}
      bannerLabels={{
        bodyText: t('banner.body_text'),
        cookiePolicyLink: t('banner.cookie_policy_link'),
        privacyPolicyLink: t('banner.privacy_policy_link'),
        manageCookies: t('banner.manage_cookies'),
        reject: t('banner.reject_non_functional'),
        accept: t('banner.accept_all'),
      }}
      dialogLabels={{
        title: t('dialog.title'),
        cancel: t('dialog.cancel'),
        save: t('dialog.save'),
        moreInfo: t('dialog.more_info'),
        analyticsToggle: t('dialog.activate_analytical'),
        marketingToggle: t('dialog.activate_marketing'),
        tabs: {
          privacy: { label: t('dialog.tabs.privacy'), content: t('dialog.tab_text.privacy') },
          functional: {
            label: t('dialog.tabs.functional'),
            content: t('dialog.tab_text.functional'),
          },
          analytics: {
            label: t('dialog.tabs.analytics'),
            content: t('dialog.tab_text.analytics'),
          },
          marketing: {
            label: t('dialog.tabs.marketing'),
            content: t('dialog.tab_text.marketing'),
          },
        },
      }}
    />
  );
};

export default TranslatedCookieConsentBanner;
